import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {cloneDeep} from 'lodash';
import {NotificationService} from '@services/notification/notification.service';
import {LanguageDetail} from '@services/translation/language-detail';
import {LANGUAGES} from '@services/translation/languages-definitions';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {UserService} from '@services/administration/user/user.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {User} from '@models/user/user';
import {Organization} from '@models/organization/organization';
import {MachineGroup} from '@models/adminstration/machine-group/machine-group';
import {USER_ROLE} from '@constants/user-role.constant';
import {EMAIL_REGEX} from '@constants/reg-exp';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {catchError, pairwise, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {Observable, of, ReplaySubject, throwError} from 'rxjs';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {MachineGroupTreeItem} from '@models/adminstration/machine-group/machine-group-tree-item';
import {UtilsService} from '@services/utils/utils.service';

@Component({
    selector: 'scitis-admin-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: false
})
export class UserEditComponent implements OnInit, OnDestroy {

  @Input()
  public user: User = new User();

  @Input()
  public loggedUser: User = new User();

  public isNewUserSuperAdmin: boolean = false;
  public isNewUser: boolean = false;
  public userForm: UntypedFormGroup = new UntypedFormGroup({});
  public submitted: boolean = false;
  public isLoadingOrganizations: boolean = false;
  public isLoadingMachineGroups: boolean = false;
  public isLoadingSubMachineGroups: boolean = false;
  public isDistributorOrHigher: boolean = false;
  public isWhiteListedUser: boolean = false;
  public isLoggedUserHigherOrEqualThanEditUser: boolean = false;
  public isSelfEditMode: boolean = false;
  public possibleRoles: string[] = new Array<string>();
  public possibleOrganizations: Organization[] = new Array<Organization>();
  public machineGroupsList: MachineGroup[] = new Array<MachineGroup>();
  public scopeMachineGroupsList: MachineGroup[] = new Array<MachineGroup>();
  public machineryGroupsTreeDataStructure: MachineGroupTreeItem | null | undefined;
  public LANGUAGES: LanguageDetail[] = LANGUAGES;
  public isSuperAdmin: boolean = false;

  private possibleMachineGroups: MachineGroup[] = new Array<MachineGroup>();
  private COMPONENT_NAME: string = 'UserEditComponent';
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
      private activeModal: NgbActiveModal,
      private translateService: TranslateService,
      private notificationService: NotificationService,
      private sortingService: SortingService,
      private userService: UserService,
      private utils: UtilsService,
      private translate: TranslateService,
      private notification: NotificationService,
      private elasticSearchService: ElasticSearchService,
      private loadingIndicator: LoadingIndicatorService,
      private translation: TranslateService,
      private menuNotificationService: MenuNotificationService) {
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.userForm.controls;
  }

  public get getPossibleMachineGroups(): MachineGroup[] {
    if (this.f.role.value !== USER_ROLE.SUPERADMIN) {
      return this.possibleMachineGroups.filter(({parentId}) => parentId > 0);
    }
    return this.possibleMachineGroups.filter(({parentId}) => !parentId || parentId < 0);
  }

  public set setPossibleMachineGroups(value: MachineGroup[]) {
    this.possibleMachineGroups = value;
  }

  public get isDisabledSaveButton(): boolean {
    if (!this.isWhiteListedUser) {
      return this.submitted ? this.userForm.invalid : this.submitted && this.userForm.invalid;
    } else {
      return this.submitted ? this.userForm.invalid || !this.user?.whiteListedMachineryGroupsMap : this.submitted && this.userForm.invalid || !this.user?.whiteListedMachineryGroupsMap;
    }
  }

  ngOnInit(): void {
    this.setPermissions();
    this.generateUserForm(this.user);

    if (this.isDistributorOrHigher) {
      this.setupMachineGroups();
    }
    this.setPossibleRoles();
    this.setPossibleOrganizations();
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave(): void {
    this.submitted = true;
    if (this.userForm.invalid) {
      this.submitted = false;
      return;
    }
    this.user.whiteListedMachineryGroupsMap = this.setUpdatedWhiteListMap();
    if (!this.hasFirebaseUserId(this.user)) {
      this.addUser();
    } else {
      this.updateUser();
    }
  }

  onUsernameChanged(): void {
    if (!this.f.username.value || this.f.username.value.length === 0) {
      this.setUserNameFieldUsedError(false);
    } else {

      this.userService.isEmailUnique(this.f.username.value)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (success) => {
          if (success == null || success.length === 0) {
            this.notification.warning(this.translate.instant('Error determining if username is used.') + this.f.username.value, this.COMPONENT_NAME);
            return;
          }

          if (success[0] === true) {
            this.notification.warning(this.translate.instant('The username is already used.') + this.f.username.value, this.COMPONENT_NAME);
            this.setUserNameFieldUsedError(true);
          } else {
            this.setUserNameFieldUsedError(false);
          }
        },
        error: (error) => {
          this.notification.error(this.translate.instant('Error determining if username is used.') + this.f.username.value, error, this.COMPONENT_NAME, true);
        }
      });
    }
  }

  private setUpdatedWhiteListMap(): any {
    const copiedTreeStructure = cloneDeep(this.machineryGroupsTreeDataStructure);
    const reducedTree = this.removeTrailingUncheckedNodes(copiedTreeStructure);
    const flattenedTree = {};

    // This transforms the nested object tree structure into the map that we need
    this.flattenTreeWithChecked(reducedTree, flattenedTree);
    return flattenedTree;
  }

  private removeTrailingUncheckedNodes(node: any): any {
    if (!node) {
      return undefined;
    }

    if (!node.children) {
      // Leaf node
      return node.checked ? node : undefined;
    }

    // Recursively process children and remove undesired nodes
    node.children = node.children.map((childNode) => this.removeTrailingUncheckedNodes(childNode)).filter(Boolean);

    // Remove this node and its parent if all children are removed and this node is not nice
    if (node.children.length === 0 && !node.checked) {
      return undefined;
    }

    return node;
  }

  private flattenTreeWithChecked(node: any, flatObject: { [key: number]: boolean } = {}): void {
    if (!node) {
      return;
    }

    // Add the current node to the flat object with the 'checked' property
    flatObject[node.id] = Boolean(node.checked);

    // Recursively process children
    if (node.children) {
      for (const childNode of node.children) {
        this.flattenTreeWithChecked(childNode, flatObject);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private setPermissions() {
    const {role} = this.loggedUser;
    this.isNewUser = !this.hasFirebaseUserId(this.user);
    this.isDistributorOrHigher = role === USER_ROLE.DISTRIBUTOR || role === USER_ROLE.ADMIN || role === USER_ROLE.TECH_ADMIN || role === USER_ROLE.SUPERADMIN;
    this.isSuperAdmin = role === USER_ROLE.SUPERADMIN;
    this.isWhiteListedUser = this.determineIfIsWhiteListedUser(this.loggedUser);
    this.isSelfEditMode = this.loggedUser.firebaseUid === this.user.firebaseUid;
    this.isLoggedUserHigherOrEqualThanEditUser = this.utils.isFirstRoleHigherOrEqualThanSecond(this.loggedUser.role, this.user.role);
  }

  private determineIfIsWhiteListedUser(loggedUser: User): boolean {
    if (loggedUser.role === USER_ROLE.SUPERADMIN) {
      return false;
    }
    const whiteListedMachineryGroups: { [key: number]: boolean } = loggedUser?.whiteListedMachineryGroupsMap;
    if (!whiteListedMachineryGroups || Object.keys(whiteListedMachineryGroups).length === 0) {
      return false;
    }

    const booleans = Object.values(whiteListedMachineryGroups);
    for (const bool of booleans) {
      if (bool) return true;
    }
    return false;


  }

  private hasFirebaseUserId(user: User): boolean {
    return !(!user.firebaseUid || user.firebaseUid.isNullOrWhitespace());
  }

  private generateUserForm(user: User): void {
    this.userForm = new UntypedFormGroup({
      username: new UntypedFormControl({
        value: user.username,
        disabled: !this.isNewUser
      }, [Validators.required, Validators.pattern(EMAIL_REGEX)]),
      displayName: new UntypedFormControl(user.displayName, [Validators.required]),
      language: new UntypedFormControl(user.language ? user.language : LANGUAGES[0].key, [Validators.required]),

      role: new UntypedFormControl(user.role, [Validators.required]),
      parentId: new UntypedFormControl(user.parentId, [Validators.required, Validators.min(1)]),
      machineGroup: new UntypedFormControl({value: user.machineGroup, disabled: !this.isDistributorOrHigher},
          [Validators.required, Validators.min(1)])
    });

    this.onRoleUpdate();
    this.onMachineGroupChange();
    this.isNewUserSuperAdmin = this.f.role.value === USER_ROLE.SUPERADMIN;
  };

  private onRoleUpdate() {
    this.f.role.valueChanges
    .pipe(takeUntil(this.destroyed$))
    .pipe(startWith(null), pairwise())
    .subscribe(([prev, next]: [USER_ROLE, USER_ROLE]) => {
      this.isNewUserSuperAdmin = next === USER_ROLE.SUPERADMIN;
      if (prev === USER_ROLE.SUPERADMIN) {
        this.f.machineGroup.reset();
      }
      if (!this.isWhiteListedUser) {
        this.machineGroupsList = this.getPossibleMachineGroups;
      } else {
        const {whiteListedMachineryGroupsMap} = this.loggedUser;
        this.machineGroupsList = this.getPossibleMachineGroups.filter(({id}) => whiteListedMachineryGroupsMap.hasOwnProperty(id));
      }
    });
  }

  private onMachineGroupChange() {
    this.f.machineGroup.valueChanges
    .pipe(
        takeUntil(this.destroyed$),
        switchMap((group) => {
          if (group) {
            this.machineryGroupsTreeDataStructure = undefined;
            this.user.whiteListedMachineryGroupsMap = {};
            return this.getMachineGroups(group);
          }
          return of(null);
        })
    )
    .subscribe((success) => {
      if (success) {
        this.handleSuccessResult(success, this.f.machineGroup.value);
      }
    });
  }

  private setPossibleRoles(): void {
    const {role} = this.loggedUser;

    const rolesMap = {
      [USER_ROLE.SUPERADMIN]: [USER_ROLE.SUPERADMIN, USER_ROLE.ADMIN, USER_ROLE.TECH_ADMIN, USER_ROLE.DISTRIBUTOR, USER_ROLE.ANALYTICS, USER_ROLE.USER, USER_ROLE.VIEWER],
      [USER_ROLE.ADMIN]: [USER_ROLE.ADMIN, USER_ROLE.DISTRIBUTOR, USER_ROLE.ANALYTICS, USER_ROLE.USER, USER_ROLE.VIEWER],
      [USER_ROLE.TECH_ADMIN]: [USER_ROLE.TECH_ADMIN, USER_ROLE.ADMIN, USER_ROLE.DISTRIBUTOR, USER_ROLE.ANALYTICS, USER_ROLE.USER, USER_ROLE.VIEWER],
      [USER_ROLE.DISTRIBUTOR]: [USER_ROLE.DISTRIBUTOR, USER_ROLE.ANALYTICS, USER_ROLE.USER, USER_ROLE.VIEWER],
      [USER_ROLE.ANALYTICS]: [USER_ROLE.ANALYTICS, USER_ROLE.USER, USER_ROLE.VIEWER],
      [USER_ROLE.USER]: [USER_ROLE.USER, USER_ROLE.VIEWER],
      [USER_ROLE.VIEWER]: [USER_ROLE.VIEWER],
    };

    this.possibleRoles = rolesMap[role];
  }

  private setPossibleOrganizations(): void {
    this.isLoadingOrganizations = true;
    const rootOrganizationId = this.isSuperAdmin ? -1 : this.loggedUser.parentId;
    this.elasticSearchService.listAllOrganizations(rootOrganizationId)
    .pipe(takeUntil(this.destroyed$))
    .subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        if (success?.result?.data && success.result.data.length > 0) {
          this.possibleOrganizations = success.result.data.sort((a, b) => this.sortingService.sortByName(a, b));
        }
        this.isLoadingOrganizations = false;
      },
      error: error => {
        const message = this.translateService.instant('Error getting organizations list.');
        this.notificationService.error(message, error, this.COMPONENT_NAME, true);
        this.isLoadingOrganizations = false;
      }
    });
  }

  private setupMachineGroups() {
    this.getMachineGroups(undefined)
    .pipe(
        takeUntil(this.destroyed$),
        switchMap((success: ElasticSearchResponseEntity) => {
          this.handleSuccessResult(success, undefined);
          return this.getMachineGroups(this.user.machineGroup).pipe(catchError(error => throwError(() => {
            return {firstCall: error};
          })));
        })
    ).subscribe({
      next: (success: ElasticSearchResponseEntity) => {
        this.handleSuccessResult(success, this.user.machineGroup);
      },
      error: (error: any) => {
        if (!error?.firstCall) {
          const message = this.translation.instant('Error getting all machinery group list.');
          this.notification.error(message, error, this.COMPONENT_NAME, true);
        } else {
          const message = this.translation.instant('Error getting machinery group list.');
          this.notification.error(message, error.firstCall, this.COMPONENT_NAME, true);
        }
        this.isLoadingMachineGroups = false;
      }
    });
  }

  private getMachineGroups(machineryGroupId: number | undefined): Observable<any> {
    this.isLoadingMachineGroups = true;
    let groupId: number | null;
    const whiteListedMachineryGroups: number[] | null = (this.isSuperAdmin || !this.loggedUser.getWhiteListedMachineryGroups()?.length) ? null : this.loggedUser.getWhiteListedMachineryGroups(true);

    if (!machineryGroupId) {
      groupId = this.isSuperAdmin ? -1 : this.loggedUser.machineGroup;
    } else {
      groupId = machineryGroupId;
    }

    return this.elasticSearchService.listAllMachineryGroups(groupId, whiteListedMachineryGroups, this.loggedUser.machineGroup);
  }

  private handleSuccessResult(success, machineryGroupId) {
    if (success?.result?.data && success.result.data.length > 0) {
      const result: ElasticSearchResult = success.result;
      if (machineryGroupId) {
        this.scopeMachineGroupsList = result.data;
        this.createTreeDataStructure(machineryGroupId, result.data);
      } else {
        this.setPossibleMachineGroups = result.data.sort((a, b) => this.sortingService.sortByName(a, b));
        this.machineGroupsList = this.getPossibleMachineGroups;
        if (this.isWhiteListedUser) {
          const accessibleWhiteListedMachineryGroups: number[] = this.loggedUser.getWhiteListedMachineryGroups(true);
          this.machineGroupsList.forEach((group) => {
            if (!accessibleWhiteListedMachineryGroups.includes(group.id)) {
              group.disabled = true;
            }
          });
        }
      }

    }
    this.isLoadingMachineGroups = false;
  }

  private addUser() {
    this.loadingIndicator.show(this.translation.instant('Loading'));
    const userBackup = cloneDeep(this.user);
    this.updateUserProperties();
    this.userService.addUser(this.getPayload())
    .pipe(takeUntil(this.destroyed$))
    .subscribe({
      next: (addedUser) => {
        const message = this.translate.instant('User') + ' ' + this.user.displayName.toBold() + ' ' +
            this.translate.instant('added') + '.';
        this.activeModal.close(addedUser);
        this.loadingIndicator.hide();
        this.notification.success(message, this.COMPONENT_NAME, false);
      },
      error: (error) => {
        this.user = userBackup;
        this.activeModal.dismiss('Error adding user');
        this.loadingIndicator.hide();
        this.notification.error(this.translate.instant('Error adding user') + ': ' + this.user.displayName.toBold(),
            error, this.COMPONENT_NAME, true);
      }
    });
  }

  private updateUser() {
    this.loadingIndicator.show(this.translation.instant('Loading'));
    const userBackup = cloneDeep(this.user);
    this.updateUserProperties();
    this.userService.updateUser(this.getPayload())
    .pipe(takeUntil(this.destroyed$))
    .subscribe({
      next: () => {
        const message = this.translate.instant('User') + ' ' + this.user.displayName.toBold() + ' ' +
            this.translate.instant('updated') + '.';
        this.activeModal.close(this.user);
        this.loadingIndicator.hide();
        this.notification.success(message, this.COMPONENT_NAME, false);
        this.updateMenu(userBackup);
      },
      error: (error) => {
        this.user = userBackup;
        this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
        this.loadingIndicator.hide();
        this.notification.error(this.translate.instant('Error updating user') +
            ': ' + this.user.displayName.toBold(), error, this.COMPONENT_NAME, true);
      }
    });
  }

  private updateMenu(userBackup: User): void {
    if (this.isSelfEditMode) {
      if (userBackup.machineGroup !== this.user.machineGroup) {
        this.menuNotificationService.broadcast(new MenuNotification(OperationType.UPDATE_USER_MACHINERY_GROUP, this.user.firebaseUid, '', ''));
      }
    }
  }

  private updateUserProperties(): void {
    this.user.username = this.f.username.value;
    this.user.displayName = this.f.displayName.value;
    this.user.language = this.f.language.value;
    this.user.role = this.f.role.value;
    this.user.parentId = this.f.parentId.value;
    this.user.machineGroup = this.f.machineGroup.value;
    const parentName = this.possibleOrganizations.find(organization => organization.id === this.f.parentId.value)?.name;
    const machineGroupName = this.possibleMachineGroups.find(group => group.id === this.f.machineGroup.value)?.name;
    this.user.parentName = parentName ?? this.user.parentName;
    this.user.machineGroupName = machineGroupName ?? this.user.machineGroupName;
  }

  private getPayload(): User {
    const payload = new User(this.user);
    if (!this.isNewUserSuperAdmin && !this.user.getWhiteListedMachineryGroups()?.length) {
      payload.whiteListedMachineryGroupsMap = {[payload.machineGroup]: false};
    }
    return payload;
  }

  private setUserNameFieldUsedError(isUsed: boolean): void {
    if (isUsed) {
      this.f.username.setErrors({used: true});
    } else {
      if (this.f?.username?.errors?.used) {
        this.f.username.setErrors({used: false});
      }
      this.f.username.updateValueAndValidity();
    }
  }

  private createTreeDataStructure(rootMachineryGroupId: number, data: MachineGroupTreeItem[]) {
    const {whiteListedMachineryGroupsMap} = this.user;

    const idMapping = data.reduce((acc, el, i) => {
      acc[el.id] = i;
      el.checked = this.f.machineGroup.dirty ? false : whiteListedMachineryGroupsMap[el.id];
      el.isVisible = true;
      el.disabled = this.isWhiteListedUser;
      return acc;
    }, {});
    let root;
    data.forEach(el => {
      if (el.id === rootMachineryGroupId) {
        root = el;
        root.disabled = this.isWhiteListedUser && !this.isNewUser && (!this.isLoggedUserHigherOrEqualThanEditUser || !this.loggedUser.whiteListedMachineryGroupsMap[el.id]);
        return;
      }
      el.disabled = this.isWhiteListedUser && !this.isNewUser && ((!this.isSelfEditMode && !this.isLoggedUserHigherOrEqualThanEditUser) || !this.loggedUser.whiteListedMachineryGroupsMap[el.id]);
      const parentEl = data[idMapping[el.parentId]];
      if (parentEl) {
        parentEl.children = [...(parentEl.children! || []), el];
      }
    });
    this.machineryGroupsTreeDataStructure = root;
  }
}
